import { Drawer, DrawerOverlay, DrawerProps, useToast } from '@chakra-ui/react';
import {
  usePostInvoiceTransactionMutation,
  usePostInvoiceMutation,
  usePutInvoiceMutation,
  useFetchInvoiceTransactionsQuery,
} from 'api';
import {
  InvoiceCreateFormDrawerContent,
  InvoiceUpdateFormDrawerContent,
  InvoiceFormRevisionDrawerContent,
} from 'features';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  InvoiceFormReviewDrawerContent,
  InvoiceModel,
  isPostInvoiceRequest,
  isPutInvoiceRequest,
} from './InvoiceFormReviewDrawerContent';
import { InvoiceResponse } from 'api/bindings/InvoiceResponse';
import { InvoiceSearchRecord } from 'api/bindings/InvoiceSearchRecord';
import { PostInvoiceTransactionRequest } from 'api/bindings/PostInvoiceTransactionRequest';
import { PutInvoiceRequest } from 'api/bindings/PutInvoiceRequest';

export enum InvoiceWorkflowStage {
  invoice = 'invoice',
  revision = 'revision',
  review = 'review',
}

export interface InvoiceFormWorkflowDrawerProps
  extends Omit<DrawerProps, 'children'> {
  invoice: InvoiceResponse | InvoiceSearchRecord | undefined;
  initialStage?: InvoiceWorkflowStage;
}

interface Form {
  currentStage: InvoiceWorkflowStage;
  header: InvoiceModel;
  includeRevisions: boolean;
  revision: PostInvoiceTransactionRequest | undefined;
  afeId?: string; // Only needed for create mode when header doesn't have afeId yet
}

// Helper function to convert InvoiceResponse or InvoiceSearchRecord to PutInvoiceRequest
function toPutInvoiceRequest(
  invoice: InvoiceResponse | InvoiceSearchRecord
): PutInvoiceRequest {
  return {
    vendorId: invoice.vendorId,
    name: invoice.name,
    description: invoice.description,
    reference: invoice.reference,
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    receiveDate: invoice.receiveDate,
    serviceThroughDate: invoice.serviceThroughDate,
    paidDate: invoice.paidDate,
  };
}

export const InvoiceFormWorkflowDrawer = (
  props: InvoiceFormWorkflowDrawerProps
) => {
  const {
    invoice,
    initialStage = InvoiceWorkflowStage.invoice,
    ...rest
  } = props;

  // Determine if this is create or update based on whether invoice is undefined
  const isUpdate = invoice !== undefined;

  // Fetches
  const { data: transactions } = useFetchInvoiceTransactionsQuery(
    { entityId: invoice?.id || '0', all: true },
    { skip: !invoice }
  );

  const toast = useToast();
  const [postInvoice] = usePostInvoiceMutation();
  const [putInvoice] = usePutInvoiceMutation();
  const [postRevision] = usePostInvoiceTransactionMutation();

  // State
  let disallowedTransactionNames = useMemo(
    () => transactions?.items.map((x) => x.name),
    [transactions?.items]
  );

  const { control, handleSubmit, setValue, watch } = useForm<Form>({
    mode: 'all',
    defaultValues: { header: invoice, currentStage: initialStage },
  });

  const onSubmit = useCallback(
    async (form: Form) => {
      console.info('submitting invoice');

      try {
        let invoiceId = invoice?.id ?? '0';

        if (initialStage === InvoiceWorkflowStage.invoice) {
          let result: any;
          if (isUpdate && isPutInvoiceRequest(form.header)) {
            result = await putInvoice({ id: invoiceId, ...form.header });
          } else if (!isUpdate && isPostInvoiceRequest(form.header)) {
            result = await postInvoice(form.header);
          } else {
            throw new Error('Invalid form header type for operation');
          }

          if ('error' in result) {
            toast({
              title: `Failed to post invoice ${form.header.name}`,
              status: 'error',
            });
            throw result.error;
          }
          invoiceId = result.data.id;
          toast({
            title: `Invoice ${form.header.name} posted!`,
            status: 'success',
          });
        }
        if (form.revision) {
          form.revision.invoiceId = invoiceId;

          const result = await postRevision({
            entityId: invoiceId,
            ...form.revision,
          });
          if ('error' in result) {
            toast({
              title: `Failed to post revision ${form.revision.name} to invoice ${form.header.name}`,
              status: 'error',
            });
            throw result.error;
          }
          toast({
            title: `Revision ${form.revision.name} posted to invoice ${form.header.name}!`,
            status: 'success',
          });
        }
      } catch {}
    },
    [
      invoice?.id,
      initialStage,
      isUpdate,
      toast,
      putInvoice,
      postInvoice,
      postRevision,
    ]
  );

  const [currentStage, header, revision, formAfeId] = watch([
    'currentStage',
    'header',
    'revision',
    'afeId',
  ]);

  // Determine the afeId based on create vs update mode
  const afeId = useMemo(() => {
    if (isUpdate && invoice) {
      // For updates, get afeId from the invoice prop
      return invoice.afeId;
    } else {
      // For creates, get afeId from the form state or header if available
      return (
        formAfeId || (header && 'afeId' in header ? header.afeId : undefined)
      );
    }
  }, [isUpdate, invoice, formAfeId, header]);

  const content = useMemo(() => {
    switch (currentStage) {
      case InvoiceWorkflowStage.invoice:
        return (
          <Controller
            control={control}
            name="header"
            render={(p) => {
              if (isUpdate && invoice) {
                return (
                  <InvoiceUpdateFormDrawerContent
                    invoice={toPutInvoiceRequest(invoice)}
                    onCancel={props.onClose}
                    onSubmit={(form) => {
                      console.debug('header', form);
                      p.field.onChange(form);
                      const nextStage = form.includeRevision
                        ? InvoiceWorkflowStage.revision
                        : InvoiceWorkflowStage.review;
                      setValue('currentStage', nextStage);
                      console.debug('form changed');
                    }}
                  />
                );
              } else {
                return (
                  <InvoiceCreateFormDrawerContent
                    onCancel={props.onClose}
                    onSubmit={(form) => {
                      console.debug('header', form);
                      p.field.onChange(form);
                      // Store afeId for revision stage
                      setValue('afeId', form.afeId);
                      const nextStage = form.includeRevision
                        ? InvoiceWorkflowStage.revision
                        : InvoiceWorkflowStage.review;
                      setValue('currentStage', nextStage);
                      console.debug('form changed');
                    }}
                  />
                );
              }
            }}
          />
        );
      case InvoiceWorkflowStage.revision:
        return (
          <Controller
            control={control}
            name="revision"
            render={(p) => (
              <InvoiceFormRevisionDrawerContent
                afeId={afeId!}
                // invoiceId={header && 'id' in header ? header.id : ''}
                // invoiceId={}
                disallowedNames={disallowedTransactionNames}
                onCancel={props.onClose}
                onSubmit={(form) => {
                  p.field.onChange(form);
                  const nextStage = InvoiceWorkflowStage.review;
                  setValue('currentStage', nextStage);
                }}
              />
            )}
          />
        );
      case InvoiceWorkflowStage.review:
        return (
          <InvoiceFormReviewDrawerContent
            afeName="" // TODO: This should be fetched based on afeId
            initialStage={initialStage}
            header={header}
            revision={revision}
            onClose={props.onClose}
            onSubmit={async (f) => {
              console.info('submit called', f);
              if (f.desiredNextStage) {
                setValue('currentStage', f.desiredNextStage);
              } else {
                try {
                  await handleSubmit(onSubmit)();
                  props.onClose();
                } catch (err: any) {}
              }
            }}
          />
        );
      default:
        return <></>;
    }
  }, [
    currentStage,
    control,
    initialStage,
    header,
    revision,
    props,
    setValue,
    handleSubmit,
    onSubmit,
    isUpdate,
    invoice,
    afeId,
    disallowedTransactionNames,
  ]);

  return (
    <Drawer size="xl" {...rest}>
      <DrawerOverlay />
      {content}
    </Drawer>
  );
};
